<template>
  <div class="gg-fields">
    <!-- Fields loaded -->
    <template v-if="sections.length > 0">
      <section
        v-for="section in sections"
        :key="section.key"
        class="gg-fields__section"
      >
        <h3 v-if="section.label" class="gg-fields__group">{{ section.label }}</h3>
        <div class="gg-fields__grid">
          <GloboGraphicsFieldRenderer
            v-for="field in section.fields"
            :key="field.name"
            :field="field"
            :value="fieldValues[field.name]"
            @input="$emit('field-input', { name: field.name, value: $event })"
            @open-image-picker="$emit('open-image-picker', $event)"
          />
        </div>
      </section>

      <button
        v-if="hiddenFieldCount > 0"
        type="button"
        class="gg-fields__toggle"
        @click="showHiddenFields = !showHiddenFields"
      >
        <v-icon size="16" class="mr-1">
          {{ showHiddenFields ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}
        </v-icon>
        {{ showHiddenFields ? 'Esconder' : 'Mostrar' }}
        {{ hiddenFieldCount }} campo{{ hiddenFieldCount > 1 ? 's' : '' }} oculto{{ hiddenFieldCount > 1 ? 's' : '' }}
      </button>
    </template>

    <!-- Loading -->
    <div v-else-if="loading" class="gg-fields__state">
      <v-progress-circular indeterminate size="20" width="2" class="mr-3" />
      <span>Carregando campos do template…</span>
    </div>

    <!-- Search matched nothing -->
    <div v-else-if="hasFields && search" class="gg-fields__state">
      Nenhum campo encontrado para “{{ search }}”.
    </div>

    <!-- Every field is hidden in the scene -->
    <div v-else-if="hasFields" class="gg-fields__state">
      Todos os campos deste template são ocultos na cena.
      <button type="button" class="gg-fields__toggle ml-2" @click="showHiddenFields = true">
        Mostrar mesmo assim
      </button>
    </div>

    <!-- Template has no editable fields at all -->
    <div v-else class="gg-fields__state">
      Este template não possui campos editáveis.
    </div>
  </div>
</template>

<script>
/**
 * Dynamic field form for a Vizrt template: a two-column grid per group, with the
 * group name as a flat always-expanded header. Templates whose fields don't fall
 * into groups render as a single headerless grid, and the group the heuristic
 * labels "Geral"/"Configuração" leads the form without a header — those fields
 * describe the graphic as a whole rather than one of its sections.
 */
import { tryLetterSuffixGrouping, tryNumericGrouping } from "@/utils/fieldGrouping";
import GloboGraphicsFieldRenderer from "./GloboGraphicsFieldRenderer.vue";

// Groups the heuristic derives for whole-graphic fields; they lead the form
// unlabelled instead of getting a header of their own.
const LEAD_GROUP_LABELS = ["Geral", "Configuração"];

function normalize(text) {
  return String(text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(new RegExp("[\\u0300-\\u036f]", "g"), "");
}

export default {
  name: "GloboGraphicsSceneFields",
  components: { GloboGraphicsFieldRenderer },
  props: {
    model: { type: Object, default: null },
    fieldValues: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    // Free-text filter over field names, labels and current values.
    search: { type: String, default: "" },
  },
  data() {
    return { showHiddenFields: false };
  },
  computed: {
    allFields() {
      return this.model?.fields || [];
    },
    hasFields() {
      return this.allFields.length > 0;
    },
    hiddenFieldCount() {
      return this.allFields.filter((f) => f.visibility === "hidden").length;
    },
    groupedFields() {
      const fields = this.allFields;
      if (fields.length < 10) return { grouped: false };
      return (
        tryLetterSuffixGrouping(fields) || tryNumericGrouping(fields) || { grouped: false }
      );
    },
    // Flattens grouping + visibility + search into the sections actually rendered.
    sections() {
      const raw = this.groupedFields.grouped
        ? this.groupedFields.groups.map((group, index) => ({
            key: group.key,
            label: index === 0 && LEAD_GROUP_LABELS.includes(group.label) ? "" : group.label,
            fields: group.fields,
          }))
        : [{ key: "_flat", label: "", fields: this.allFields }];

      return raw
        .map((section) => ({ ...section, fields: section.fields.filter(this.matches) }))
        .filter((section) => section.fields.length > 0);
    },
  },
  methods: {
    matches(field) {
      if (field.visibility === "hidden" && !this.showHiddenFields) return false;
      const term = normalize(this.search).trim();
      if (!term) return true;
      const haystack = normalize(
        `${field.label || ""} ${field.name || ""} ${this.fieldValues[field.name] || ""}`
      );
      return haystack.includes(term);
    },
  },
};
</script>

<style lang="scss" scoped>
.gg-fields {
  display: flex;
  flex-direction: column;
  gap: var(--sc-space-8);
  width: 100%;
}

.gg-fields__section {
  display: flex;
  flex-direction: column;
  gap: var(--sc-space-6);
}

.gg-fields__group {
  margin: 0;
  font-size: 20px;
  font-weight: var(--sc-weight-semi);
  line-height: 24px;
  color: var(--sc-gg-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gg-fields__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: var(--sc-space-8);
  row-gap: var(--sc-space-4);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
}

.gg-fields__state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sc-space-6);
  border-radius: var(--sc-radius-sm);
  background: var(--sc-gg-overlay-image);
  color: var(--sc-gg-text-tertiary);
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.07px;
  text-align: center;

  ::v-deep .v-progress-circular {
    color: var(--sc-gg-text-tertiary);
  }
}

.gg-fields__toggle {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  font-size: 14px;
  color: var(--sc-gg-accent);

  .v-icon {
    color: var(--sc-gg-accent) !important;
  }

  &:hover {
    text-decoration: underline;
  }
}
</style>
